import {
  BUTTON_TEST_IDS,
  DEFAULT_HIT_MAP_INTERVAL_MS,
  MIN_HIT_MAP_INTERVAL_MS,
  MPX_SANDBOX_DISPENSARY_ID,
} from './constants';
import type {
  DiagnosticsFlagConfig,
  ElementDescriptor,
  HitMapColumnResult,
  HitMapResult,
  OffsetProbeResult,
  RectSnapshot,
} from './types';

/**
 * Rounds numbers to fixed decimals (default 1) to keep JSON payloads compact and bounded.
 * Safely guards NaN and non-finite values.
 */
export const round = (n: number, decimals = 1): number => {
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    return 0;
  }
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
};

/**
 * Validates and extracts diagnostics configuration from the LD flag payload.
 * Returns enabled=false unless the value is an object with enabled === true.
 */
export const parseDiagnosticsFlag = (flagValue: unknown): DiagnosticsFlagConfig => {
  if (!flagValue || typeof flagValue !== 'object' || Array.isArray(flagValue)) {
    return { enabled: false, overlay: false, hitMapIntervalMs: DEFAULT_HIT_MAP_INTERVAL_MS };
  }

  const obj = flagValue as Record<string, unknown>;
  if (obj.enabled !== true) {
    return { enabled: false, overlay: false, hitMapIntervalMs: DEFAULT_HIT_MAP_INTERVAL_MS };
  }

  const overlay = obj.overlay === true;
  const interval =
    typeof obj.hitMapIntervalMs === 'number' && Number.isFinite(obj.hitMapIntervalMs)
      ? Math.max(MIN_HIT_MAP_INTERVAL_MS, obj.hitMapIntervalMs)
      : DEFAULT_HIT_MAP_INTERVAL_MS;

  return {
    enabled: true,
    overlay,
    hitMapIntervalMs: interval,
  };
};

/**
 * Hardcoded code allowlist gate.
 * In production, only the MPX Sandbox dispensary is permitted, even if the LD flag is misconfigured.
 * In non-production environments (local dev, test, staging), any dispensary is permitted.
 */
export const isDiagnosticsAllowed = ({
  dispensaryId,
  appEnv,
}: {
  dispensaryId?: string | null;
  appEnv?: string;
}): boolean => {
  if (appEnv === 'production') {
    return dispensaryId === MPX_SANDBOX_DISPENSARY_ID;
  }
  return true;
};

/**
 * Extracts a privacy-safe element descriptor.
 * Never accesses textContent, innerText, or form input values.
 */
export const describeElement = (target: EventTarget | Node | null): ElementDescriptor => {
  if (!target || !(target instanceof Element)) {
    return {};
  }

  const descriptor: ElementDescriptor = {
    tag: target.tagName.toLowerCase(),
  };

  if (target.id) {
    descriptor.id = target.id;
  }

  const testid = target.getAttribute('data-testid');
  if (testid) {
    descriptor.testid = testid;
  }

  const role = target.getAttribute('role');
  if (role) {
    descriptor.role = role;
  }

  const ariaLabel = target.getAttribute('aria-label');
  if (ariaLabel) {
    descriptor.ariaLabel = ariaLabel;
  }

  // getAttribute covers SVG too, whose `className` is an SVGAnimatedString rather than a string.
  const className = target.getAttribute('class');
  if (className) {
    descriptor.className = className.slice(0, 50);
  }

  return descriptor;
};

export const getRectSnapshot = (el: Element): RectSnapshot => {
  const r = el.getBoundingClientRect();
  return {
    top: round(r.top),
    right: round(r.right),
    bottom: round(r.bottom),
    left: round(r.left),
    width: round(r.width),
    height: round(r.height),
  };
};

/**
 * Run-length encodes an array of categorical strings (e.g. ['outside', 'outside', 'cart'] -> 'outside:2,cart:1').
 * Caps result string at 500 characters.
 */
export const encodeRunLengths = (items: string[]): string => {
  if (items.length === 0) {
    return '';
  }

  const runs: string[] = [];
  let current = items[0];
  let count = 1;

  for (let i = 1; i < items.length; i += 1) {
    if (items[i] === current) {
      count += 1;
    } else {
      runs.push(`${current}:${count}`);
      current = items[i];
      count = 1;
    }
  }
  runs.push(`${current}:${count}`);

  return runs.join(',').slice(0, 500);
};

/**
 * Vertical offset probe on pointer events near the bar.
 * Scans elementFromPoint vertically across [barRect.top - 250, barRect.bottom + 250] in 5px steps
 * to discover where the hit-test target resolves to the bar relative to its layout rect.
 */
export const probeBarHitRange = (clientX: number, bar: HTMLElement, barRect: DOMRect): OffsetProbeResult => {
  const visualYMin = round(barRect.top);
  const visualYMax = round(barRect.bottom);
  const windowH = typeof window !== 'undefined' ? window.innerHeight : 1000;
  const startY = Math.max(0, Math.floor(barRect.top - 250));
  const endY = Math.min(windowH, Math.ceil(barRect.bottom + 250));
  let hitYMin: number | null = null;
  let hitYMax: number | null = null;

  if (typeof document !== 'undefined' && typeof document.elementFromPoint === 'function') {
    for (let y = startY; y <= endY; y += 5) {
      const el = document.elementFromPoint(clientX, y);
      if (el && bar.contains(el)) {
        hitYMin ??= y;
        hitYMax = y;
      }
    }
  }

  return {
    x: round(clientX),
    sampledYMin: round(startY),
    sampledYMax: round(endY),
    barHitYMin: hitYMin !== null ? round(hitYMin) : null,
    barHitYMax: hitYMax !== null ? round(hitYMax) : null,
    visualBarYMin: visualYMin,
    visualBarYMax: visualYMax,
    offsetY: hitYMin !== null ? round(hitYMin - barRect.top) : null,
  };
};

/**
 * Scans a multi-column hit-test grid:
 * - 5 percentage columns across the bar (10px step)
 * - 1 column centered on each present button in BUTTON_TEST_IDS (4px step)
 *
 * Logs startY, endY, barRect, and per-column x, stepPx, and run-length encoded hit results.
 */
export const computeHitMap = (bar: HTMLElement, barRect: DOMRect): HitMapResult => {
  const windowH = typeof window !== 'undefined' ? window.innerHeight : 1000;
  const startY = Math.max(0, Math.floor(barRect.top - 200));
  const endY = Math.min(windowH, Math.ceil(barRect.bottom + 200));
  const barRectSnapshot = getRectSnapshot(bar);
  const columns: Record<string, HitMapColumnResult> = {};

  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') {
    return {
      startY: round(startY),
      endY: round(endY),
      barRect: barRectSnapshot,
      columns,
    };
  }

  const sampleColumn = (x: number, stepPx: number): string => {
    const samples: string[] = [];
    for (let y = startY; y <= endY; y += stepPx) {
      const el = document.elementFromPoint(x, y);
      if (!el) {
        samples.push('none');
      } else if (!bar.contains(el)) {
        if (el.closest('[data-testid*="product"]')) {
          samples.push('product');
        } else {
          samples.push('outside');
        }
      } else if (el.closest('[data-testid="kiosk-pro-go-to-cart-button"]')) {
        samples.push('cart');
      } else if (el.closest('[data-testid="kiosk-pro-end-session-button"]')) {
        samples.push('end_session');
      } else if (el.closest('[data-testid="kiosk-pro-login-button"]')) {
        samples.push('login');
      } else if (el.closest('[data-testid="kiosk-pro-order-history-button"]')) {
        samples.push('order_history');
      } else {
        samples.push('bar_other');
      }
    }
    return encodeRunLengths(samples);
  };

  // 1. Five percentage columns across bar width (10px vertical step)
  const pctColumns = [
    { label: 'col_10pct', pct: 0.1 },
    { label: 'col_30pct', pct: 0.3 },
    { label: 'col_50pct', pct: 0.5 },
    { label: 'col_70pct', pct: 0.7 },
    { label: 'col_90pct', pct: 0.9 },
  ];

  pctColumns.forEach(({ label, pct }) => {
    const x = round(barRect.left + barRect.width * pct);
    const stepPx = 10;
    columns[label] = {
      x,
      stepPx,
      encoded: sampleColumn(x, stepPx),
    };
  });

  // 2. High-resolution column per button present in bar (4px vertical step)
  BUTTON_TEST_IDS.forEach((btnId) => {
    const btn = bar.querySelector(`[data-testid="${btnId}"]`);
    if (btn) {
      const btnRect = btn.getBoundingClientRect();
      const x = round(btnRect.left + btnRect.width / 2);
      const stepPx = 4;
      columns[btnId] = {
        x,
        stepPx,
        encoded: sampleColumn(x, stepPx),
      };
    }
  });

  return {
    startY: round(startY),
    endY: round(endY),
    barRect: barRectSnapshot,
    columns,
  };
};
